import React, { FC, PropsWithChildren, useMemo } from 'react'
import Box from '@mui/material/Box'
import { DataLoad, PropsInjector } from 'src/components'
import { useGatewaysListRequest, useGatewayStatesRequest } from 'src/hooks/api'
import { useQueryParamsState } from 'src/hooks/ui'
import { Gateway } from 'src/api'
import { GatewayColumnsDataGrid } from 'src/components/columns/gateway'
import { CommonGatewayList } from '../gateway/gatewayList/commonGatewaysList'

const columns = [
  GatewayColumnsDataGrid.DeviceId,
  GatewayColumnsDataGrid.IMEI,
  GatewayColumnsDataGrid.DevEUI,
  GatewayColumnsDataGrid.ExternalDeviceId,
  GatewayColumnsDataGrid.OnlineStatus,
  GatewayColumnsDataGrid.LastPingAt,
  GatewayColumnsDataGrid.Name,
  GatewayColumnsDataGrid.State,
  GatewayColumnsDataGrid.Project,
  GatewayColumnsDataGrid.CreatedAt,
]

interface ContractorGatewaysListProps {
  gatewayListLoading?: boolean;
  count?: number;
  data?: Gateway[];
}

const ContractorGatewaysList: FC<ContractorGatewaysListProps> = ({
  gatewayListLoading = false,
  count = 0,
  data = [],
}) => (
  <Box height="500px">
    <CommonGatewayList
      columns={columns}
      count={count}
      data={data}
      isLoading={gatewayListLoading}
    />
  </Box>
)

interface ContractorGatewayLoaderProps {
  contractorId: number;
}

const ContractorGatewayLoader: FC<PropsWithChildren<ContractorGatewayLoaderProps>> = ({
  contractorId,
  children,
}) => {
  const params = useQueryParamsState()
  const { data: states = [] } = useGatewayStatesRequest()

  const { isInitialLoading, isError, data, error } = useGatewaysListRequest({
    params: {
      ...params,
      contractorId,
    },
  })

  const rows = useMemo(
    () => (data?.rows || []).map((row) => ({
      ...row,
      state: states.find((s) => s.id === row.stateId),
    })),
    [data, states],
  )

  const props: ContractorGatewaysListProps = {
    gatewayListLoading: isInitialLoading,
    count: data?.count || 0,
    data: rows,
  }

  return (
    <DataLoad isLoading={false} isError={isError} errorMessage={error?.message}>
      <PropsInjector props={props}>{children}</PropsInjector>
    </DataLoad>
  )
}

interface ContractorGatewaysProps {
  contractorId: number;
}

export const ContractorGateways: FC<ContractorGatewaysProps> = ({ contractorId }) => (
  <ContractorGatewayLoader contractorId={contractorId}>
    <ContractorGatewaysList />
  </ContractorGatewayLoader>
)
